import { VocabularyIrregularVerb } from 'app/types';
import {
  VOCAB_STATUSES,
  WordStatus,
  hexToRgba,
  statusColor,
} from 'components/vocabularyWordsView/meta';

// Re-export shared status enum + color helpers so verb consumers don't have
// to dual-import from both modules. The lifecycle (NEW/REPEAT/LEARNED/ACTIVE)
// is identical for words and verbs.
export { VOCAB_STATUSES, hexToRgba, statusColor };
export type { WordStatus };

/**
 * Derive the 4-state lifecycle from the legacy two-boolean payload that
 * `VocabularyIrregularVerb` still uses. The BE will eventually expose a single
 * enum field (mirroring `DictionaryWord.status`); until then verbs must be
 * coerced. `REPEAT` is unreachable from the booleans and round-trips as LEARNED.
 */
export const statusOfVerb = (
  verb: Pick<VocabularyIrregularVerb, 'isLearned' | 'isActiveSpeech'>,
): WordStatus => {
  if (verb.isActiveSpeech) return 'ACTIVE';
  if (verb.isLearned) return 'LEARNED';
  return 'NEW';
};

export type StatusFilterValue = 'all' | WordStatus;

export const STATUS_FILTER_CHIPS: Array<{
  value: StatusFilterValue;
  label: string;
  status?: WordStatus;
}> = [
  { value: 'all', label: 'Усі' },
  { value: 'ACTIVE', label: 'Активне', status: 'ACTIVE' },
  { value: 'LEARNED', label: 'Вивчено', status: 'LEARNED' },
  { value: 'REPEAT', label: 'Повторити', status: 'REPEAT' },
  { value: 'NEW', label: 'Нові', status: 'NEW' },
];

/** UA-language column labels for the v2 settings popover (verbs version). */
export const VERB_COLUMN_LABELS_UA: Record<string, string> = {
  number: '№',
  levelBased: 'Рівень',
  infinitive: 'Infinitive',
  pastSimple: 'Past Simple',
  pastParticiple: 'Past Participle',
  translation: 'Переклад',
  description: 'Опис',
  status: 'Статус',
  addedAt: 'Додано',
};

/**
 * Ukrainian noun-form helper: "дієслово / дієслова / дієслів" based on count.
 * Mirrors `slivWord` from the words module but for verbs.
 */
export const slivVerb = (n: number): string => {
  const d = n % 10;
  const h = n % 100;
  if (d === 1 && h !== 11) return 'дієслово';
  if (d >= 2 && d <= 4 && (h < 12 || h > 14)) return 'дієслова';
  return 'дієслів';
};

export const fmtAddedDate = (iso?: string | null): string => {
  if (!iso) return '—';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return '—';
  const dd = String(dt.getDate()).padStart(2, '0');
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const yyyy = dt.getFullYear();
  const hh = String(dt.getHours()).padStart(2, '0');
  const min = String(dt.getMinutes()).padStart(2, '0');
  return `${dd}.${mm}.${yyyy} ${hh}:${min}`;
};

export const fmtN = (n: number): string => n.toLocaleString('uk-UA');

/**
 * CEFR-coloured pill colours for the level column. Mirrors the wordset
 * pages so a B1-verb in the dictionary table and on the browse pages have
 * the same colour.
 */
export const LEVEL_COLORS: Record<string, string> = {
  A1: '#27CCBC',
  A2: '#3FA98C',
  B1: '#488BFF',
  B2: '#5B5BD6',
  C1: '#3B2C93',
  C2: '#2A2540',
};

export const getLevelColor = (level?: string | null): string =>
  (level && LEVEL_COLORS[level]) || '#7A7F88';
